// Copyright 2026, Auxilio Technologies LLC

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runsAPI, Order, DispatchRun } from '../services/api';
import { useShopStore } from '../store/shopStore';
import { SLOT_ORDER, STATUS_BADGE, formatMoney, getLocalDateString } from '../lib/orderUtils';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  TrashIcon,
  TruckIcon,
  PlusIcon,
  ArrowPathIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

interface Driver { id: string; username: string; email: string; }

const RUN_BADGE: Record<string, string> = {
  planning: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  dispatched: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  complete: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const RouteBuilder: React.FC = () => {
  const { t } = useTranslation();
  const { shops, activeShopId } = useShopStore();
  const sourceShop = shops.find((s) => s.id === activeShopId);
  const [date, setDate] = useState(getLocalDateString());
  const [slot, setSlot] = useState('');
  const [ready, setReady] = useState<Order[]>([]);
  const [runs, setRuns] = useState<DispatchRun[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newRunSlot, setNewRunSlot] = useState('before_11');
  const [newRunDriver, setNewRunDriver] = useState('');

  const driverName = (id?: string) => drivers.find((d) => d.id === id)?.username || t('routes.unassigned');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      runsAPI.readyOrders(date, slot || undefined),
      runsAPI.list(date, slot || undefined),
    ])
      .then(([r1, r2]) => {
        setReady(r1.data.orders || []);
        setRuns(r2.data.runs || []);
      })
      .catch(() => setError(t('auth.errorGeneric')))
      .finally(() => setLoading(false));
  }, [date, slot, t]);

  useEffect(() => {
    runsAPI.drivers().then((r) => setDrivers(r.data.drivers || [])).catch(() => setDrivers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createRun = async () => {
    setError('');
    try {
      await runsAPI.create({ time_slot: newRunSlot, run_date: date, driver_user_id: newRunDriver || null });
      setNewRunDriver('');
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  const addToRun = async (runId: string, orderId: string) => {
    await runsAPI.addStops(runId, [orderId]);
    load();
  };

  const move = async (run: DispatchRun, index: number, dir: -1 | 1) => {
    const ids = run.stops.map((s) => s.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await runsAPI.reorder(run.id, ids);
    load();
  };

  const removeStop = async (runId: string, stopId: string) => { await runsAPI.removeStop(runId, stopId); load(); };
  const assign = async (runId: string, driverId: string) => { await runsAPI.assign(runId, driverId); load(); };

  const dispatch = async (runId: string) => {
    setError('');
    try {
      await runsAPI.dispatch(runId);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  const planningRuns = runs.filter((r) => r.status === 'planning');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('routes.title')}</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('routes.subtitle')}</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-lg bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
          <ArrowPathIcon className="h-4 w-4" /> {t('routes.refresh')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">{t('routes.date')}</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">{t('routes.slot')}</label>
          <select value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
            <option value="">{t('routes.allSlots')}</option>
            {SLOT_ORDER.map((s) => <option key={s} value={s}>{t(`order.slot.${s}`)}</option>)}
          </select>
        </div>
      </div>

      {/* Source shop (route origin) */}
      {sourceShop && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm dark:border-rose-800 dark:bg-rose-900/20">
          <MapPinIcon className="h-4 w-4 text-rose-600" />
          <span className="text-gray-700 dark:text-gray-300">
            {t('routes.source')}: <strong>{sourceShop.name}</strong>
            {sourceShop.address ? ` — ${sourceShop.address}` : ''}
          </span>
        </div>
      )}

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{error}</div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Ready orders */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('routes.readyTitle')}</h2>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
          ) : ready.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">{t('routes.readyEmpty')}</div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {ready.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center gap-2 px-5 py-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">{o.recipient_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{o.delivery_address}</div>
                  </div>
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">{t(`order.slot.${o.delivery_time_slot}`)}</span>
                  {planningRuns.length === 0 ? (
                    <span className="text-xs text-gray-400">{t('routes.createFirst')}</span>
                  ) : (
                    <select
                      defaultValue=""
                      onChange={(e) => e.target.value && addToRun(e.target.value, o.id)}
                      className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">{t('routes.addToRun')}</option>
                      {planningRuns.map((r) => (
                        <option key={r.id} value={r.id}>#{r.run_number} · {driverName(r.driver_user_id)}</option>
                      ))}
                    </select>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Runs */}
        <div className="space-y-4">
          {/* New run */}
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-3 font-semibold text-gray-900 dark:text-white">{t('routes.newRun')}</h2>
            <div className="flex flex-wrap items-end gap-2">
              <select value={newRunSlot} onChange={(e) => setNewRunSlot(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                {SLOT_ORDER.map((s) => <option key={s} value={s}>{t(`order.slot.${s}`)}</option>)}
              </select>
              <select value={newRunDriver} onChange={(e) => setNewRunDriver(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                <option value="">{t('routes.pickDriver')}</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.username}</option>)}
              </select>
              <button onClick={createRun} className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700">
                <PlusIcon className="h-4 w-4" /> {t('routes.create')}
              </button>
            </div>
          </div>

          {runs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white py-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400">
              {t('routes.noRuns')}
            </div>
          ) : (
            runs.map((run) => (
              <div key={run.id} className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <TruckIcon className="h-5 w-5 text-rose-600" />
                    <span className="font-semibold text-gray-900 dark:text-white">#{run.run_number}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">· {t(`order.slot.${run.time_slot}`)} · {run.stops.length} {t('routes.stops')}</span>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RUN_BADGE[run.status] || RUN_BADGE.planning}`}>{t(`routes.${run.status}`)}</span>
                </div>

                <div className="px-5 py-3">
                  {/* Driver assignment */}
                  {run.status === 'planning' ? (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('routes.driver')}:</span>
                      <select value={run.driver_user_id || ''} onChange={(e) => assign(run.id, e.target.value)} className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                        <option value="">{t('routes.pickDriver')}</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{d.username}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="mb-3 text-sm text-gray-600 dark:text-gray-300">{t('routes.driver')}: <strong>{driverName(run.driver_user_id)}</strong></div>
                  )}

                  {/* Stops */}
                  {run.stops.length === 0 ? (
                    <p className="text-sm text-gray-400">—</p>
                  ) : (
                    <ol className="space-y-1">
                      {run.stops.map((s, i) => (
                        <li key={s.id} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-700/40">
                          <span className="w-5 text-center text-sm font-semibold text-gray-400">{i + 1}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{s.order?.recipient_name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{s.order?.delivery_address}</div>
                          </div>
                          {s.order?.status && (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[s.order.status] || STATUS_BADGE.new}`}>{t(`order.status.${s.order.status}`)}</span>
                          )}
                          {run.status === 'planning' && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => move(run, i, -1)} title={t('routes.moveUp')} className="text-gray-400 hover:text-rose-600"><ArrowUpIcon className="h-4 w-4" /></button>
                              <button onClick={() => move(run, i, 1)} title={t('routes.moveDown')} className="text-gray-400 hover:text-rose-600"><ArrowDownIcon className="h-4 w-4" /></button>
                              <button onClick={() => removeStop(run.id, s.id)} title={t('routes.remove')} className="text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}

                  {run.status === 'planning' && (
                    <button
                      onClick={() => dispatch(run.id)}
                      disabled={!run.driver_user_id || run.stops.length === 0}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      <TruckIcon className="h-4 w-4" /> {t('routes.dispatch')}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteBuilder;
